import { lazy, Suspense, useEffect, useState } from 'react';
import { Link, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import { AudioWaveform, Moon, Sun } from 'lucide-react';
import { brand } from './engine/catalog';
import { HomePage } from './pages/Home';
import { EffectsPage } from './pages/Effects';
import { PrivacyPage, TermsPage, ContactPage, NotFoundPage } from './pages/Static';
import { ZoomHint } from './components/ZoomHint';

const EditorPage = lazy(() => import('./pages/Editor'));

type Theme = 'system' | 'light' | 'dark';

function readTheme(): Theme {
  try {
    const t = localStorage.getItem('vc-theme');
    return t === 'light' || t === 'dark' ? t : 'system';
  } catch {
    return 'system';
  }
}

declare global {
  interface Window { googlefc?: { showRevocationMessage?: () => void; callbackQueue?: unknown[] } }
}

export function App() {
  const [theme, setTheme] = useState<Theme>(readTheme);
  const location = useLocation();

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'system') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', theme);
    try { localStorage.setItem('vc-theme', theme); } catch { /* storage unavailable */ }
  }, [theme]);

  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);

  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia?.('(prefers-color-scheme: dark)').matches);

  const openPrivacyChoices = (e: React.MouseEvent) => {
    const fc = window.googlefc;
    if (fc?.showRevocationMessage) {
      e.preventDefault();
      fc.showRevocationMessage();
    }
  };

  return (
    <>
      <a href="#main" className="skip-link">Skip to content</a>
      <ZoomHint />
      <header className="site-header">
        <div className="container site-header__inner">
          <Link to="/" className="brand" aria-label={`${brand.name} home`}>
            <span className="brand__mark"><AudioWaveform size={18} /></span>
            <span className="brand__name">{brand.name}</span>
            <span className="brand__short">{brand.shortName}</span>
          </Link>
          <nav className="nav" aria-label="Main">
            <NavLink to="/editor">Editor</NavLink>
            <NavLink to="/effects">Effects</NavLink>
            <NavLink to="/privacy" className="hide-sm">Privacy</NavLink>
            <button
              className="btn btn--ghost btn--icon"
              aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
              onClick={() => setTheme(isDark ? 'light' : 'dark')}
            >
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          </nav>
        </div>
      </header>
      <main id="main">
        <Suspense fallback={<div className="container" style={{ padding: 40 }}>Loading editor…</div>}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/editor" element={<EditorPage />} />
            <Route path="/effects" element={<EffectsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
      <footer className="site-footer">
        <div className="container site-footer__inner">
          <span>© {new Date().getFullYear()} {brand.name}. Your media is processed on your device.</span>
          <nav aria-label="Footer">
            <Link to="/privacy">Privacy</Link>
            <a href="/privacy#ads" onClick={openPrivacyChoices}>Privacy choices</a>
            <Link to="/terms">Terms</Link>
            <Link to="/contact">Contact</Link>
          </nav>
        </div>
      </footer>
    </>
  );
}
